// Caching Service for Mining Data

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

class CacheService {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private defaultTTL = 30000; // 30 seconds default

  // Set cache entry
  set<T>(key: string, data: T, ttl: number = this.defaultTTL): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl
    };
    
    this.cache.set(key, entry);
    console.log(`Cache set: ${key} (TTL: ${ttl}ms)`);
  }

  // Get cache entry
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }

    // Check if entry has expired
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      console.log(`Cache expired: ${key}`);
      return null;
    }

    console.log(`Cache hit: ${key}`);
    return entry.data as T;
  }

  // Check if key exists and is valid
  has(key: string): boolean {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return false;
    }

    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  // Delete cache entry
  delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    if (deleted) {
      console.log(`Cache deleted: ${key}`);
    }
    return deleted;
  }

  // Clear all cache
  clear(): void {
    this.cache.clear();
    console.log('Cache cleared');
  }

  // Get cache statistics
  getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > entry.ttl) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    });

    return {
      totalEntries: this.cache.size,
      validEntries,
      expiredEntries,
      hitRate: 0 // Would need to track hits/misses for accurate rate
    };
  }

  // Clean expired entries
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > entry.ttl) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => {
      this.cache.delete(key);
    });

    if (keysToDelete.length > 0) {
      console.log(`Cache cleanup: removed ${keysToDelete.length} expired entries`);
    }
  }
}

// Create singleton cache instance
export const cacheService = new CacheService();

// Mining-specific cache keys and TTLs
export const CACHE_KEYS = {
  DASHBOARD_STATS: 'dashboard_stats',
  POOL_STATS: 'pool_stats',
  MULTIPORT_DATA: 'multiport_data',
  MINER_STATUS: 'miner_status',
  PROFITABILITY_AVERAGE: 'profitability_average',
  CRYPTO_PROGRESS: 'crypto_progress'
};

export const CACHE_TTL = {
  DASHBOARD_STATS: 30000, // 30 seconds
  POOL_STATS: 60000, // 1 minute
  MULTIPORT_DATA: 120000, // 2 minutes
  MINER_STATUS: 15000, // 15 seconds
  PROFITABILITY_AVERAGE: 300000, // 5 minutes
  CRYPTO_PROGRESS: 45000 // 45 seconds
};

// Cached API wrapper functions
export const cachedMiningServices = {
  // Get dashboard stats with caching
  getDashboardStats: async (apiCall: () => Promise<any>) => {
    const cached = cacheService.get(CACHE_KEYS.DASHBOARD_STATS);
    if (cached) {
      return cached;
    }

    try {
      const data = await apiCall();
      cacheService.set(CACHE_KEYS.DASHBOARD_STATS, data, CACHE_TTL.DASHBOARD_STATS);
      return data;
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      throw error;
    }
  },

  // Get pool stats with caching
  getPoolStats: async (apiCall: () => Promise<any>) => {
    const cached = cacheService.get(CACHE_KEYS.POOL_STATS);
    if (cached) {
      return cached;
    }

    try {
      const data = await apiCall();
      cacheService.set(CACHE_KEYS.POOL_STATS, data, CACHE_TTL.POOL_STATS);
      return data;
    } catch (error) {
      console.error('Failed to fetch pool stats:', error);
      throw error;
    }
  },

  // Get multiport data with caching
  getMultiportData: async (apiCall: () => Promise<any>) => {
    const cached = cacheService.get(CACHE_KEYS.MULTIPORT_DATA);
    if (cached) {
      return cached;
    }

    try {
      const data = await apiCall();
      cacheService.set(CACHE_KEYS.MULTIPORT_DATA, data, CACHE_TTL.MULTIPORT_DATA);
      return data;
    } catch (error) {
      console.error('Failed to fetch multiport data:', error);
      throw error;
    }
  },

  // Get miner status with caching
  getMinerStatus: async (apiCall: () => Promise<any>) => {
    const cached = cacheService.get(CACHE_KEYS.MINER_STATUS);
    if (cached) {
      return cached;
    }

    try {
      const data = await apiCall();
      cacheService.set(CACHE_KEYS.MINER_STATUS, data, CACHE_TTL.MINER_STATUS);
      return data;
    } catch (error) {
      console.error('Failed to fetch miner status:', error);
      throw error;
    }
  },

  // Get crypto progress with caching
  getCryptoProgress: async (apiCall: () => Promise<any>) => {
    const cached = cacheService.get(CACHE_KEYS.CRYPTO_PROGRESS);
    if (cached) {
      return cached;
    }

    try {
      const data = await apiCall();
      cacheService.set(CACHE_KEYS.CRYPTO_PROGRESS, data, CACHE_TTL.CRYPTO_PROGRESS);
      return data;
    } catch (error) {
      console.error('Failed to fetch crypto progress:', error);
      throw error;
    }
  }
};

// Auto cleanup interval (every 5 minutes)
setInterval(() => {
  cacheService.cleanup();
}, 300000);

export default cacheService;